import * as React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { AppContext } from '../../App';
import {
  Tooltip,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Box,
  DialogContentText,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import { FormControlStyled, FormLabelStyled } from '../common/Dialog/DialogGlynt';
import useUpdateVendor from '../hooks/vendors/useUpdateVendor';
import useAddVendorAlias from '../hooks/vendors/useAddVendorAlias';
import useRemoveVendorAlias from '../hooks/vendors/useRemoveVendorAlias';
import { Vendor, VendorType, DetectionPattern, PatternMode } from '../../types/vendor-types';
import useCreateVendorDetectionPattern from '../hooks/vendors/useCreateVendorDetectionPattern';
import useUpdateVendorDetectionPattern from '../hooks/vendors/useUpdateVendorDetectionPattern';
import useDeleteVendorDetectionPattern from '../hooks/vendors/useDeleteVendorDetectionPattern';
import useVendorSearch from '../hooks/vendors/useVendorSearch';
import useDebouncedValue from '../hooks/useDebouncedValue';
import useRenameVendor from '../hooks/vendors/useRenameVendor';
import useMergeVendor from '../hooks/vendors/useMergeVendor';
import { Dialog } from '@mui/material';
import DetectionPatternDialog, { DetectionPatternFormData } from './DetectionPatternDialog';
// Extract all validation error messages from error.message JSON
const extractValidationMessages = (error: unknown): string[] => {
  const messages: string[] = [];
  try {
    const parsed = JSON.parse(((error as any)?.message as string) || '{}');
    Object.keys(parsed || {}).forEach((key) => {
      const arr = (parsed as any)[key];
      if (Array.isArray(arr)) arr.forEach((el) => messages.push(el));
    });
  } catch {}
  return messages;
};

export interface IVendorEditProps {
  vendor: Vendor;
}

interface FormData {
  name: string;
  abbreviated: string;
  aliases: string[];
  vendor_type: VendorType;
}

const VENDOR_TYPE_OPTIONS: { value: VendorType; label: string }[] = [
  { value: null, label: 'None' },
  { value: 'supply', label: 'Supply' },
  { value: 'delivery', label: 'Delivery' },
];

const VendorEdit: React.FC<IVendorEditProps> = ({ vendor }) => {
  const [open, setOpen] = React.useState(false);
  const [aliases, setAliases] = React.useState<string[]>(vendor.aliases || []);
  const [newAlias, setNewAlias] = React.useState('');
  const [patterns, setPatterns] = React.useState<DetectionPattern[]>(
    vendor.detection_patterns || []
  );

  const [patternDialogOpen, setPatternDialogOpen] = React.useState(false);
  const [editingPattern, setEditingPattern] = React.useState<DetectionPattern | null>(null);

  const [currentName, setCurrentName] = React.useState(vendor.name);
  const [currentAbbreviated, setCurrentAbbreviated] = React.useState(vendor.abbreviated || '');
  const [currentVendorType, setCurrentVendorType] = React.useState<VendorType>(
    vendor.vendor_type || null
  );
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = React.useState(false);

  // Rename/merge state
  const [renameInputValue, setRenameInputValue] = React.useState('');
  const [selectedTargetVendor, setSelectedTargetVendor] = React.useState<Vendor | null>(null);
  const [mergeConfirmed, setMergeConfirmed] = React.useState(false);

  const { setToast } = React.useContext(AppContext);
  const theme = useTheme();

  // Server-side search for the merge-target autocomplete. Only queries while
  // the edit dialog is open and the user is typing a target name.
  const debouncedMergeQuery = useDebouncedValue(renameInputValue, 400);
  const { data: mergeMatches, isFetching: mergeLoading } = useVendorSearch({
    search: debouncedMergeQuery,
    limit: 20,
    enabled: open && debouncedMergeQuery.trim().length > 0,
  });
  const otherVendors = (mergeMatches?.results || []).filter((v) => v.id !== vendor.id);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    defaultValues: {
      name: vendor.name,
      abbreviated: vendor.abbreviated || '',
      aliases: vendor.aliases || [],
      vendor_type: vendor.vendor_type || null,
    },
    mode: 'onChange',
  });

  const updateVendorMutation = useUpdateVendor({
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Vendor updated successfully.',
      });
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: 'Failed to update vendor. Please try again.',
      });
    },
  });

  const addAliasMutation = useAddVendorAlias({
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Alias added successfully.',
      });
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: 'Failed to add alias. Please try again.',
      });
    },
  });

  const removeAliasMutation = useRemoveVendorAlias({
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Alias removed successfully.',
      });
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: 'Failed to remove alias. Please try again.',
      });
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
    setAliases(vendor.aliases || []);
    setPatterns(vendor.detection_patterns || []);
    setCurrentName(vendor.name);
    setCurrentAbbreviated(vendor.abbreviated || '');
    setCurrentVendorType(vendor.vendor_type || null);
    setRenameInputValue('');
    setSelectedTargetVendor(null);
    setMergeConfirmed(false);
    reset({
      name: vendor.name,
      abbreviated: vendor.abbreviated || '',
      aliases: vendor.aliases || [],
      vendor_type: vendor.vendor_type || null,
    });
  };

  const handleClose = () => {
    // Check for unsaved changes - use form's isDirty state, new aliases, and rename input
    const hasRenameChanges =
      renameInputValue.trim() !== '' || selectedTargetVendor !== null;
    const hasUnsavedChanges = isDirty || newAlias.trim() !== '' || hasRenameChanges;

    if (hasUnsavedChanges) {
      setShowUnsavedChangesDialog(true);
    } else {
      setOpen(false);
      setNewAlias('');
      setRenameInputValue('');
      setSelectedTargetVendor(null);
      setMergeConfirmed(false);
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedChangesDialog(false);
    setOpen(false);
    setNewAlias('');
    setRenameInputValue('');
    setSelectedTargetVendor(null);
    setMergeConfirmed(false);
  };

  const handleCancelClose = () => {
    setShowUnsavedChangesDialog(false);
  };

  const handleAddAlias = () => {
    if (newAlias.trim() && !aliases.includes(newAlias.trim())) {
      addAliasMutation.mutate(
        { id: vendor.id, alias: newAlias.trim() },
        {
          onSuccess: () => {
            setAliases([...aliases, newAlias.trim()]);
            setNewAlias('');
          },
        }
      );
    }
  };

  const handleRemoveAlias = (aliasToRemove: string) => {
    removeAliasMutation.mutate(
      { id: vendor.id, alias: aliasToRemove },
      {
        onSuccess: () => {
          setAliases(aliases.filter((alias) => alias !== aliasToRemove));
        },
      }
    );
  };

  const onSubmit = (data: FormData) => {
    const updateData: { name?: string; abbreviated?: string; vendor_type?: VendorType } = {};

    if (data.name !== vendor.name) {
      updateData.name = data.name;
    }

    if (data.abbreviated !== (vendor.abbreviated || '')) {
      updateData.abbreviated = data.abbreviated;
    }

    if (data.vendor_type !== vendor.vendor_type) {
      updateData.vendor_type = data.vendor_type;
    }

    if (Object.keys(updateData).length > 0) {
      updateVendorMutation.mutate(
        { id: vendor.id, data: updateData },
        {
          onSuccess: () => {
            // Reset the form with the updated values to clear the isDirty state
            const updatedVendor = {
              ...vendor,
              name: data.name,
              abbreviated: data.abbreviated,
              vendor_type: data.vendor_type,
            };

            reset({
              name: updatedVendor.name,
              abbreviated: updatedVendor.abbreviated || '',
              aliases: updatedVendor.aliases || [],
              vendor_type: updatedVendor.vendor_type || null,
            });

            // Update the current state variables
            setCurrentName(updatedVendor.name);
            setCurrentAbbreviated(updatedVendor.abbreviated || '');
            setCurrentVendorType(updatedVendor.vendor_type || null);
          },
        }
      );
    }
  };
  const createPatternMutation = useCreateVendorDetectionPattern({
    onSuccess: () => {
      setToast({ variant: 'success', open: true, message: 'Pattern saved.' });
    },
    onError: (error: unknown) => {
      const errorResponses = extractValidationMessages(error);
      const fallbackMsg =
        (error && typeof error === 'object' && 'message' in error && (error as any).message) || '';
      const details = errorResponses.length
        ? errorResponses.join(' | ')
        : String(fallbackMsg || '');
      setToast({
        variant: 'error',
        open: true,
        message: `Failed to save pattern.${details ? ' ' + details : ''}`,
      });
    },
  });

  const updatePatternMutation = useUpdateVendorDetectionPattern({
    onSuccess: () => {
      setToast({ variant: 'success', open: true, message: 'Pattern updated.' });
    },
    onError: (error: unknown) => {
      const errorResponses = extractValidationMessages(error);
      const fallbackMsg =
        (error && typeof error === 'object' && 'message' in error && (error as any).message) || '';
      const details = errorResponses.length
        ? errorResponses.join(' | ')
        : String(fallbackMsg || '');
      setToast({
        variant: 'error',
        open: true,
        message: `Failed to update pattern.${details ? ' ' + details : ''}`,
      });
    },
  });

  const deletePatternMutation = useDeleteVendorDetectionPattern({
    onSuccess: () => {
      setToast({ variant: 'success', open: true, message: 'Pattern deleted.' });
    },
    onError: () => {
      setToast({ variant: 'error', open: true, message: 'Failed to delete pattern.' });
    },
  });

  const renameVendorMutation = useRenameVendor({
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Vendor renamed successfully.',
      });
      setOpen(false);
      setRenameInputValue('');
      setSelectedTargetVendor(null);
      setMergeConfirmed(false);
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: 'Failed to rename vendor. Please try again.',
      });
    },
  });

  const mergeVendorMutation = useMergeVendor({
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Vendors merged successfully.',
      });
      setOpen(false);
      setRenameInputValue('');
      setSelectedTargetVendor(null);
      setMergeConfirmed(false);
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: 'Failed to merge vendors. Please try again.',
      });
    },
  });

  const handleRenameOrMerge = () => {
    if (selectedTargetVendor) {
      // Merge into existing vendor
      mergeVendorMutation.mutate({
        sourceId: vendor.id,
        targetVendorId: selectedTargetVendor.id,
      });
    } else if (renameInputValue.trim() && renameInputValue.trim() !== vendor.name) {
      // Rename to new name
      renameVendorMutation.mutate({
        id: vendor.id,
        newName: renameInputValue.trim(),
      });
    }
  };

  const isLoading =
    updateVendorMutation.isPending ||
    addAliasMutation.isPending ||
    removeAliasMutation.isPending ||
    createPatternMutation.isPending ||
    updatePatternMutation.isPending ||
    deletePatternMutation.isPending ||
    renameVendorMutation.isPending ||
    mergeVendorMutation.isPending;

  const openNewPatternDialog = () => {
    setEditingPattern(null);
    setPatternDialogOpen(true);
  };

  const openEditPatternDialog = (p: DetectionPattern) => {
    setEditingPattern(p);
    setPatternDialogOpen(true);
  };

  const closePatternDialog = () => {
    setPatternDialogOpen(false);
  };

  return (
    <>
      <Tooltip placement="top" title="Edit Vendor" disableFocusListener disableTouchListener>
        <IconButton
          data-cy={`btn-edit-vendor-${vendor.name}`}
          onClick={!isLoading ? handleClickOpen : undefined}
          disabled={isLoading}
          sx={{
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.main + '20',
            },
          }}
        >
          <EditIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <DialogGlynt
        open={open}
        closeModal={handleClose}
        loading={isLoading}
        cypressId="dialog-vendor-edit"
      >
        <DialogTitleGlynt
          title={`Edit ${vendor.name}`}
          closeModal={handleClose}
          loading={isLoading}
        />
        <DialogContentGlynt>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/*
            <FormControlStyled fullWidth>
              <FormLabelStyled>VENDOR NAME</FormLabelStyled>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Vendor name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Enter vendor name"
                    variant="outlined"
                    size="small"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    disabled
                    onChange={(e) => {
                      field.onChange(e);
                      setCurrentName(e.target.value);
                    }}
                    data-cy="vendor-name-input"
                  />
                )}
              />
            </FormControlStyled>
           

            <FormControlStyled fullWidth>
              <FormLabelStyled>ABBREVIATED NAME</FormLabelStyled>
              <Controller
                name="abbreviated"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Enter abbreviated name"
                    variant="outlined"
                    size="small"
                    error={!!errors.abbreviated}
                    helperText={errors.abbreviated?.message}
                    disabled={isLoading}
                    onChange={(e) => {
                      field.onChange(e);
                      setCurrentAbbreviated(e.target.value);
                    }}
                    data-cy="vendor-abbreviated-input"
                  />
                )}
              />
            </FormControlStyled>
               */}

            {/*
            <FormControlStyled fullWidth>
              <FormLabelStyled>VENDOR TYPE</FormLabelStyled>
              <Controller
                name="vendor_type"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value || ''}
                    onChange={(e) => {
                      const newValue =
                        e.target.value === '' ? null : (e.target.value as VendorType);
                      field.onChange(newValue);
                      setCurrentVendorType(newValue);
                    }}
                    variant="outlined"
                    size="small"
                    error={!!errors.vendor_type}
                    disabled
                    data-cy="vendor-type-select"
                  >
                    {VENDOR_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value || 'null'} value={option.value || ''}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControlStyled>
            

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSubmit(onSubmit)}
                disabled={isLoading || !isDirty}
                sx={{
                  backgroundColor: '#65BBA9',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#65BBA9',
                    color: '#FFFFFF',
                  },
                  '&:disabled': {
                    backgroundColor: '#E0E0E0',
                    color: '#B0B0B0',
                  },
                }}
                data-cy="save-vendor-details-button"
              >
                SAVE CHANGES
              </Button>
            </Box>
            */}

            <FormControlStyled fullWidth>
              <FormLabelStyled>RENAME</FormLabelStyled>
              <Autocomplete
                freeSolo
                options={otherVendors}
                filterOptions={(opts) => opts}
                loading={mergeLoading}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.name
                }
                inputValue={renameInputValue}
                onInputChange={(_, newInputValue) => {
                  setRenameInputValue(newInputValue);
                  // Clear selected vendor if user is typing a new name
                  if (selectedTargetVendor && newInputValue !== selectedTargetVendor.name) {
                    setSelectedTargetVendor(null);
                    setMergeConfirmed(false);
                  }
                }}
                value={selectedTargetVendor}
                onChange={(_, newValue) => {
                  if (typeof newValue === 'string') {
                    setSelectedTargetVendor(null);
                    setRenameInputValue(newValue);
                  } else {
                    setSelectedTargetVendor(newValue);
                    setRenameInputValue(newValue?.name || '');
                  }
                  setMergeConfirmed(false);
                }}
                disabled={isLoading}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li {...optionProps} key={option.id || key}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Typography>{option.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.aliases?.length || 0} alias{(option.aliases?.length || 0) !== 1 ? 'es' : ''}
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Enter new name or select existing vendor"
                    variant="outlined"
                    size="small"
                    data-cy="vendor-rename-input"
                  />
                )}
                data-cy="autocomplete-vendor-rename"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Type a new name to rename, or select an existing vendor to merge into it.
              </Typography>

              {selectedTargetVendor && vendor.mat_count > 0 && (
                <Alert
                  severity="warning"
                  sx={{ mt: 2 }}
                  data-cy="alert-merge-warning"
                >
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    This will update {vendor.mat_count.toLocaleString()} MAT row{vendor.mat_count !== 1 ? 's' : ''}.
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={mergeConfirmed}
                        onChange={(e) => setMergeConfirmed(e.target.checked)}
                        disabled={isLoading}
                        data-cy="checkbox-confirm-merge"
                      />
                    }
                    label="I understand"
                  />
                </Alert>
              )}

              {!selectedTargetVendor && renameInputValue.trim() && renameInputValue.trim() !== vendor.name && vendor.mat_count > 0 && (
                <Alert
                  severity="info"
                  sx={{ mt: 2 }}
                  data-cy="alert-rename-info"
                >
                  <Typography variant="body2">
                    Renaming this vendor will update {vendor.mat_count.toLocaleString()} MAT row{vendor.mat_count !== 1 ? 's' : ''}.
                  </Typography>
                </Alert>
              )}

              {(renameInputValue.trim() && renameInputValue.trim() !== vendor.name) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  {selectedTargetVendor ? (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleRenameOrMerge}
                      disabled={isLoading || (vendor.mat_count > 0 && !mergeConfirmed)}
                      sx={{
                        backgroundColor: '#ed6c02',
                        color: '#FFFFFF',
                        '&:hover': {
                          backgroundColor: '#c55a02',
                        },
                        '&:disabled': {
                          backgroundColor: '#E0E0E0',
                          color: '#B0B0B0',
                        },
                      }}
                      data-cy="btn-merge-vendor"
                    >
                      MERGE VENDORS
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleRenameOrMerge}
                      disabled={isLoading}
                      sx={{
                        backgroundColor: '#65BBA9',
                        color: '#FFFFFF',
                        '&:hover': {
                          backgroundColor: '#65BBA9',
                        },
                        '&:disabled': {
                          backgroundColor: '#E0E0E0',
                          color: '#B0B0B0',
                        },
                      }}
                      data-cy="btn-rename-vendor"
                    >
                      RENAME VENDOR
                    </Button>
                  )}
                </Box>
              )}
            </FormControlStyled>

            <FormControlStyled fullWidth>
              <FormLabelStyled>ALIASES</FormLabelStyled>

              <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
                <TextField
                  value={newAlias}
                  onChange={(e) => setNewAlias(e.target.value)}
                  placeholder="Add new"
                  variant="outlined"
                  size="small"
                  disabled={isLoading}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddAlias();
                    }
                  }}
                  sx={{ flex: 1 }}
                  data-cy="vendor-alias-add-input"
                />
                <Button
                  variant="contained"
                  onClick={handleAddAlias}
                  disabled={isLoading || !newAlias.trim()}
                  sx={{
                    backgroundColor: '#65BBA9',
                    color: '#FFFFFF',
                    alignSelf: 'center',
                    '&:hover': {
                      backgroundColor: '#65BBA9',
                      color: '#FFFFFF',
                    },
                    '&:disabled': {
                      backgroundColor: '#E0E0E0',
                      color: '#B0B0B0',
                    },
                  }}
                  data-cy="add-vendor-alias-button"
                >
                  ADD
                </Button>
              </Box>

              {aliases.length > 0 && (
                <List
                  dense
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  {aliases.map((alias, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={`• ${alias}`}
                        data-cy={`vendor-alias-${alias}`}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '14px',
                            color: 'text.primary',
                          },
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveAlias(alias)}
                          disabled={isLoading}
                          size="small"
                          data-cy={`remove-vendor-alias-${alias}`}
                          sx={{
                            color: theme.palette.error.main,
                            '&:hover': {
                              backgroundColor: theme.palette.error.main + '20',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </FormControlStyled>

            <FormControlStyled fullWidth sx={{ mt: 3 }}>
              <FormLabelStyled>DETECTION PATTERNS</FormLabelStyled>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={openNewPatternDialog}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: '#65BBA9',
                    color: '#FFFFFF',
                    '&:hover': { backgroundColor: '#65BBA9', color: '#FFFFFF' },
                    '&:disabled': { backgroundColor: '#E0E0E0', color: '#B0B0B0' },
                  }}
                  data-cy="add-detection-pattern-button"
                >
                  ADD
                </Button>
              </Box>
              {patterns && patterns.length > 0 && (
                <List
                  dense
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  {patterns
                    .slice()
                    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
                    .map((p, index) => (
                      <ListItem key={index} sx={{ py: 0.5, pr: 5 }} data-cy={`vendor-detection-pattern-${p.label}`}>
                        {' '}
                        <ListItemSecondaryAction
                          sx={{ right: theme.spacing(1), display: 'flex', gap: 0.5 }}
                        >
                          <IconButton
                            edge="end"
                            onClick={() => openEditPatternDialog(p)}
                            disabled={isLoading}
                            size="small"
                            data-cy={`edit-vendor-detection-pattern-${p.label}`}
                            sx={{
                              color: theme.palette.primary.main,
                              '&:hover': { backgroundColor: theme.palette.primary.main + '20' },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {p.id && (
                            <IconButton
                              edge="end"
                              onClick={() =>
                                deletePatternMutation.mutate(
                                  { id: p.id },
                                  {
                                    onSuccess: () => {
                                      setPatterns((prev) => prev.filter((x) => x.id !== p.id));
                                    },
                                  }
                                )
                              }
                              disabled={isLoading}
                              size="small"
                              data-cy={`delete-vendor-detection-pattern-${p.label}`}
                              sx={{
                                color: theme.palette.error.main,
                                '&:hover': { backgroundColor: theme.palette.error.main + '20' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </ListItemSecondaryAction>
                        <ListItemText
                          primary={`• ${p.label}: ${p.pattern}`}
                          secondary={`mode: ${p.pattern_mode || 'KEYWORD'} | case: ${p.case_sensitive ? 'sensitive' : 'insensitive'} | priority: ${p.priority ?? 0}`}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '14px',
                              color: 'text.primary',
                            },
                            '& .MuiListItemText-secondary': { fontSize: '12px' },
                          }}
                        />
                      </ListItem>
                    ))}
                </List>
              )}
            </FormControlStyled>
          </form>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleSubmit(onSubmit)}
          closeModal={handleClose}
          cancelCypressId="btn-vendor-edit-modal-cancel"
          submitCypressId="btn-vendor-edit-modal-save"
          clearCypressId=""
          submitButtonText="Save Changes"
          submitLoading={isLoading}
          submitDisabled={true}
          showClearButton={false}
          showCancelButton={true}
          showSubmitButton={false}
        />
      </DialogGlynt>

      <DialogGlynt
        open={showUnsavedChangesDialog}
        closeModal={handleCancelClose}
        cypressId="dialog-unsaved-changes"
      >
        <DialogTitleGlynt title="Unsaved Changes" closeModal={handleCancelClose} />
        <DialogContentGlynt>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to close without saving?
          </DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleConfirmClose}
          closeModal={handleCancelClose}
          cancelCypressId="btn-unsaved-changes-cancel"
          submitCypressId="btn-unsaved-changes-confirm"
          clearCypressId=""
          submitButtonText="Close Without Saving"
          submitLoading={false}
          showClearButton={false}
          showCancelButton={true}
        />
      </DialogGlynt>

      <DetectionPatternDialog
        open={patternDialogOpen}
        loading={isLoading}
        pattern={editingPattern}
        vendorName={vendor.name}
        onClose={closePatternDialog}
        onSave={(data: DetectionPatternFormData) => {
          if (editingPattern && editingPattern.id) {
            updatePatternMutation.mutate(
              {
                id: editingPattern.id,
                data: {
                  label: data.label || null,
                  pattern: data.pattern,
                  pattern_mode: data.pattern_mode,
                  case_sensitive: data.case_sensitive,
                  priority: data.priority,
                },
              },
              {
                onSuccess: () => {
                  setPatterns((prev) =>
                    prev.map((p) =>
                      p.id === editingPattern.id
                        ? {
                            ...p,
                            label: data.label || undefined,
                            pattern: data.pattern,
                            pattern_mode: data.pattern_mode,
                            case_sensitive: data.case_sensitive,
                            priority: data.priority,
                          }
                        : p
                    )
                  );
                  closePatternDialog();
                },
              }
            );
          } else {
            createPatternMutation.mutate(
              {
                vendor: vendor.id,
                label: data.label || undefined,
                pattern: data.pattern,
                pattern_mode: data.pattern_mode,
                case_sensitive: data.case_sensitive,
                priority: data.priority,
              },
              {
                onSuccess: (created) => {
                  setPatterns((prev) => [...prev, created]);
                  closePatternDialog();
                },
              }
            );
          }
        }}
      />
    </>
  );
};

export default VendorEdit;
